import { Component, ElementRef, inject, input, InputSignal, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UtilityService } from '@core/services';
import { ContainsOneValidator } from '@core/validators';
import { SessionAuth } from '@legacy-models/auth/user';
import { Account } from '@legacy-models/portal/Account';
import { TPServiceRequest } from '@legacy-models/target-process/TPServiceRequest';
import { SvgIconComponent } from 'angular-svg-icon';
import posthog from 'posthog-js';
import { ToastComponent } from '@core/components';

interface RequestItem {
  id: string;
  label: string;
  val?: string;
  subcategories?: RequestItem[] | null;
  children?: RequestItem[] | null;
}

interface SupportRequestContent {
  title: string;
  description: string;
  categories: RequestItem[];
}

interface RequestForm {
  primary: FormControl<string | null>;
  secondary: FormControl<string | null>;
  tertiary: FormArray<FormControl<boolean>>;
  message: FormControl<string>;
}

type RequestControlName = 'primary' | 'secondary';

@Component({
  selector: 'oke-request',
  imports: [ReactiveFormsModule, SvgIconComponent, ToastComponent],
  templateUrl: './request.html',
  styleUrl: './request.scss'
})
export class Request implements OnInit {
  private _timeoutId: ReturnType<typeof setTimeout> | number;
  readonly $content: InputSignal<SupportRequestContent> = input.required<SupportRequestContent>({ alias: 'content' });
  manager: FormGroup<RequestForm> = new FormGroup<RequestForm>({
    primary: new FormControl<string | null>(null),
    secondary: new FormControl<string | null>(null, Validators.required),
    tertiary: new FormArray<FormControl<boolean>>([]),
    message: new FormControl('', { nonNullable: true, validators: Validators.required })
  });
  primaryItems: RequestItem[] = [];
  secondaryItems: RequestItem[] = [];
  tertiaryItems: RequestItem[] = [];
  isInTransit = false;
  isSuccessfulSubmission: boolean;
  transactionStatus: string | undefined;
  private readonly _elem: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly _utilitySvc: UtilityService = inject(UtilityService);

  ngOnInit(): void {
    this.primaryItems = this._getPrimaryItems();
    const changeHandlers: Record<RequestControlName, (value: string | null, ms: number) => Promise<null> | void> = {
      primary: this._onPrimaryChange.bind(this),
      secondary: this._onSecondaryChange.bind(this)
    };
    (Object.keys(changeHandlers) as RequestControlName[]).forEach(formControlName => {
      this.manager.get(formControlName)!.valueChanges.subscribe(newValue => {
        const result = changeHandlers[formControlName](newValue, 150);
        if (result) {
          result.then(this._doScroll.bind(this));
        }
      });
    });
  }

  private _getPrimaryItems(): RequestItem[] {
    const sessionUser = JSON.parse(sessionStorage.getItem('currUser') as string) as SessionAuth;
    const contactBusinessSegments = (sessionUser.Contact?.BusinessSegments ?? []).filter(
      (businessSegment): businessSegment is string => typeof businessSegment === 'string'
    );

    return this.$content()
      .categories.map(({ label, id, val }) => {
        if (!val || contactBusinessSegments.includes(val)) {
          return { label, id };
        }
        return null;
      })
      .filter((item): item is RequestItem => item !== null);
  }

  private _onPrimaryChange(newPrimaryValue: string | null, ms: number | null = null): Promise<null> | void {
    this.tertiaryItems = [];
    this.manager.get('secondary')!.setValue(null, { emitEvent: false });
    this.managerTertiaryItems.clear({ emitEvent: false });
    this.manager.get('secondary')!.addValidators(Validators.required);
    this.managerTertiaryItems.clearValidators();
    if (newPrimaryValue === "Other / I don't know") {
      ['secondary', 'tertiary'].forEach(formControlName => this.manager.get(formControlName)!.clearValidators());
    }
    ['secondary', 'tertiary'].forEach(formControlName => this.manager.get(formControlName)!.updateValueAndValidity());
    const theSecondaryItems = this.$content().categories.find(({ id }) => id === this.manager.get('primary')!.value);
    if (theSecondaryItems?.subcategories) {
      this.secondaryItems = theSecondaryItems.subcategories.map(({ label, id }) => {
        return { label, id };
      });
    } else {
      this.secondaryItems = [];
    }
    if (ms !== null) {
      return new Promise(resolve => {
        this._timeoutId = setTimeout(() => resolve(null), ms);
      });
    }
  }

  private _onSecondaryChange(newSecondaryValue: string | null, ms: number | null = null): Promise<null> | void {
    this.tertiaryItems = [];
    this.managerTertiaryItems.clear({ emitEvent: false });
    this.managerTertiaryItems.clearValidators();
    const selectedPrimaryCategory = this.$content().categories.find(
      ({ id }) => id === this.manager.get('primary')!.value
    );
    const selectedSecondaryCategory = selectedPrimaryCategory?.subcategories?.find(
      ({ label }) => label === newSecondaryValue
    );

    if (selectedSecondaryCategory?.children?.length) {
      selectedSecondaryCategory.children.forEach(tertiaryChild => {
        this.managerTertiaryItems.push(new FormControl(false, { nonNullable: true }));
        this.tertiaryItems.push({
          id: tertiaryChild.id,
          label: tertiaryChild.label,
          val: tertiaryChild.val
        });
      });
      this.managerTertiaryItems.addValidators(ContainsOneValidator());
    }
    this.managerTertiaryItems.updateValueAndValidity();
    if (ms !== null) {
      return new Promise(resolve => {
        this._timeoutId = setTimeout(() => resolve(null), ms);
      });
    }
  }

  private _doScroll(): void {
    const parentElement = this._elem.nativeElement.parentElement;
    if (!parentElement) {
      return;
    }
    parentElement.scrollTop = parentElement.scrollHeight;
    clearTimeout(this._timeoutId);
  }

  onSubmit(): void {
    if (!this.manager.valid) {
      return;
    }
    // this.isInTransit = true;
    posthog.capture('help:request_submit');
    this.transactionStatus = undefined;
    const sessionUser = JSON.parse(sessionStorage.getItem('currUser') as string) as SessionAuth;
    const currentAccount = JSON.parse(sessionStorage.getItem('currAcct') as string) as Account;
    const getValue = (formControlName: 'primary' | 'secondary' | 'message'): string =>
      this.manager.get(formControlName)!.value ?? '';
    const assetLocations: Record<string, string[]> = {};
    this.managerTertiaryItems.value.forEach((isSelected, boolIdx) => {
      if (!isSelected) {
        return;
      }
      const assetLocation = this.tertiaryItems[boolIdx].val;
      if (!assetLocation) {
        return;
      }
      if (!Object.prototype.hasOwnProperty.call(assetLocations, assetLocation)) {
        assetLocations[assetLocation] = [`${this.tertiaryItems[boolIdx].id}`];
      } else {
        assetLocations[assetLocation].push(`${this.tertiaryItems[boolIdx].id}`);
      }
    });
    const postObject: TPServiceRequest = {
      requesterEmail: sessionUser.Contact?.Email ?? '',
      businessSegment: getValue('primary'),
      requestType: getValue('secondary'),
      userName: `${sessionUser.Contact?.FirstName ?? ''} ${sessionUser.Contact?.LastName ?? ''}`.trim(),
      accountName: currentAccount.AccountName,
      comments: getValue('message'),
      assetAreas: assetLocations
    };
    this._utilitySvc.postSNRequest(postObject).subscribe(
      (res: unknown) => {
        const responseMessage = typeof res === 'object' && res !== null && 'message' in res ? res.message : undefined;
        this.transactionStatus =
          typeof res === 'string' ? res : typeof responseMessage === 'string' ? responseMessage : '';
        //this.transactionStatus = res;
        this.isSuccessfulSubmission = true;
        this.manager.reset();
        this.primaryItems = this._getPrimaryItems();
        this.secondaryItems = [];
        this.tertiaryItems = [];
        setTimeout(() => {
          this.transactionStatus = undefined;
        }, 5000);
      },
      () => {
        this.transactionStatus = 'There was an error submitting your request. Please try submitting again, or';
        this.isSuccessfulSubmission = false;
      },
      () => (this.isInTransit = false)
    );
  }

  get managerTertiaryItems(): FormArray<FormControl<boolean>> {
    return this.manager.controls.tertiary;
  }
}
